import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";

import store from "@pages/Statistics/store";

import { StatisticsContext } from "@pages/Statistics/context";

import { useResource } from "@hooks/useResource";

import {
    getAdvancedPeriodStringParam,
    getRoundedArrayNumbers,
    getStringsArrayUniqueValues,
    getTypeParam,
    getSalesModesQueryString,
    getUntranslatedString,
} from "@helpers/general";

import ErrorPage from "@components/Common/ErrorPage";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";

import DonutChart from "@pages/Statistics/CommonCharts/DonutChart";
import LegendPopoverWrapper from "@pages/Statistics/CommonCharts/DonutChart/LegendPopoverWrapper";

export default function Top10FamiliesCol() {
    const { t } = useTranslation();
    const {
        period,
        type,
        advancedDate,
        selections,
        setPercentageData,
    } = React.useContext(StatisticsContext);

    const statisticsStore = useSnapshot(store);

    const [data, setData] = React.useState<number[]>([]);
    const [labels, setLabels] = React.useState<string[]>([]);

    const { resourceData: top10FamiliesData, error } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/sales-mode/percentage-families?type=${getTypeParam(
            type
        )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }${
            getSalesModesQueryString(statisticsStore.salesModeCards.modes)
                .length > 0
                ? `&sales-mode=${getSalesModesQueryString(
                      statisticsStore.salesModeCards.modes.map(
                          getUntranslatedString
                      )
                  )}`
                : ""
        }`
    );

    React.useEffect(() => {
        let localData: number[] = [];
        let localLabels: string[] = [];
        top10FamiliesData.data?.forEach((item: any) => {
            localData.push(item.value);
            localLabels.push(item.designation);
        });

        setData(getRoundedArrayNumbers(localData.filter((item) => item !== 0)));
        setLabels(
            getStringsArrayUniqueValues(
                localLabels.length > 0
                    ? localLabels.map((item) => t(item))
                    : [t("No data")]
            )
        );
        setPercentageData!(localData);
    }, [top10FamiliesData, t, setPercentageData]);

    const donutChartPurchaseFamiliesData = {
        data,
        labels:
            labels.length > 0
                ? getStringsArrayUniqueValues(labels).map((label) => t(label))
                : [t("No data")],
    };

    return (
        <React.Fragment>
            {error ? (
                <ErrorPage />
            ) : (
                <CustomErrorBoundary>
                    <DonutChart
                        title={t("Top 10 families")}
                        data={donutChartPurchaseFamiliesData}
                    >
                        <LegendPopoverWrapper
                            title={t("Top 10 families")}
                            labels={labels || []}
                        />
                    </DonutChart>
                </CustomErrorBoundary>
            )}
        </React.Fragment>
    );
}
